import React, { useState } from "react";
import { Cap, Level } from "../utils/rbacCatalog";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { HiOutlineTrash } from "react-icons/hi";
import { Spinner } from "../components/atoms/Spinner";
import { ModalShell } from "../components/atoms/ModalShell";
import { CustomButton } from "../components/atoms/CustomButton";
import useAuth from "../hooks/useAuth";
import { PlanTier, usePermissions } from "../utils/permissions";
import FeatureUpgradePrompt from "../components/atoms/FeatureUpgradePrompt";
import { PlansModal } from "../components/modals/PlansModal";
import {
  useCreateEvent,
  useDeleteEvent,
  useGetEventProcessId,
  useGetEvents,
  useUpdateEvent,
} from "../hooks/entity";
import { Event } from "../types/EventPaymentTypes";
import ConfirmModal from "../components/public/ConfirmModal";
import {
  eventToUIForm,
  UIEventForm,
  uiFormToEventPayload,
} from "../utils/eventHelpers";
import EventModal from "../components/molecules/EventModal";
import EventBanner from "../components/molecules/EventPayments/EventBanner";
import EventEmptyState from "../components/molecules/EventPayments/EventEmptyState";
import EventCard from "../components/molecules/EventPayments/EventCard";
import ShareEventModal from "../components/molecules/EventPayments/ShareEventModal";

const EventPayments: React.FC = () => {
  const { t } = useTranslation();
  const { customer_identification, user } = useAuth();
  const entityIdentification = user?.entity_identification || "";
  const permissions = usePermissions(user);
  const planTier = permissions.getPlanTier();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPlansModal, setShowPlansModal] = useState(false);
  const [showUnlockEventsModal, setShowUnlockEventsModal] = useState(false);
  const [showPaidTicketUpgradeModal, setShowPaidTicketUpgradeModal] =
    useState(false);
  const [targetPlanTier, setTargetPlanTier] = useState<PlanTier>(
    PlanTier.PREMIUM
  );

  const [editEventData, setEditEventData] = useState<UIEventForm | undefined>();
  const [eventCode, setEventCode] = useState("");

  const {
    data: events,
    isLoading,
    refetch,
  } = useGetEvents(customer_identification, entityIdentification);

  const createEventMutation = useCreateEvent();
  const updateEventMutation = useUpdateEvent();
  const deleteEventMutation = useDeleteEvent();

  const { data: processIdData, refetch: refetchProcessId } =
    useGetEventProcessId(
      customer_identification,
      entityIdentification,
      selectedEvent || ""
    );

  const eventsData = (events || {}) as Record<string, Event>;

  const handleCreateEvent = () => {
    setEditEventData(undefined);
    setEventCode("");
    setShowCreateModal(true);
  };

  const handleEditEvent = (code: string) => {
    const event = eventsData[code];
    if (event) {
      setEditEventData(eventToUIForm(event));
      setEventCode(code);
      setShowEditModal(true);
    }
  };

  const handleDeleteEventClick = (code: string) => {
    setEventToDelete(code);
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;

    try {
      await deleteEventMutation.mutateAsync({
        customer_identification,
        entity_identification: entityIdentification,
        event_id: eventToDelete,
      });

      await refetch();
      setShowDeleteConfirmModal(false);
      setEventToDelete(null);
    } catch (error: any) {
      setShowDeleteConfirmModal(false);
      setEventToDelete(null);
    }
  };

  const handleShareEvent = (code: string) => {
    setSelectedEvent(code);
    setShowShareModal(true);
    setTimeout(() => refetchProcessId(), 100);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setEditEventData(undefined);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditEventData(undefined);
  };

  const handleSubmitEvent = async (eventForm: UIEventForm) => {
    if (planTier === PlanTier.FREE) {
      setShowUnlockEventsModal(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const eventPayload = uiFormToEventPayload(eventForm);

      if (eventCode) {
        await updateEventMutation.mutateAsync({
          customer_identification,
          entity_identification: entityIdentification,
          event_id: eventCode,
          eventData: eventPayload,
        });
      } else {
        await createEventMutation.mutateAsync({
          customer_identification,
          entity_identification: entityIdentification,
          eventData: eventPayload,
        });
      }

      setShowCreateModal(false);
      setShowEditModal(false);
      setEditEventData(undefined);
      await refetch();
    } catch (error: any) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowPaidTicketUpgrade = () => {
    setShowPaidTicketUpgradeModal(true);
  };

  const shareUrl = processIdData?.element?.process_identification
    ? `${process.env.REACT_APP_HOST_URL}/process/${processIdData.element.process_identification}`
    : "";

  const selectedEventName =
    selectedEvent && eventsData[selectedEvent]
      ? eventsData[selectedEvent].event_data?.settings?.event_name || ""
      : "";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  const eventsList = Object.entries(eventsData).map(([code, event]) => ({
    code,
    ...eventToUIForm(event),
    created_at: event.created_at,
  }));

  return (
    <div className="event-payments-page">
      <Helmet>
        <title>{t("dashboard.layout.item-18")} - Super User Manager</title>
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              {t("dashboard.layout.item-18")}
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {t("components.eventPayments.pageDescription")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {eventsList.length > 0 &&
              permissions.can(Cap.EVENTS, Level.WRITE) && (
                <CustomButton
                  title={t("components.eventPayments.actions.createEvent")}
                  onClick={handleCreateEvent}
                  variant="primary"
                  icon="plus"
                />
              )}
          </div>
        </div>

        <EventBanner />

        {eventsList.length === 0 ? (
          <EventEmptyState
            onCreateEvent={
              permissions.can(Cap.EVENTS, Level.WRITE)
                ? handleCreateEvent
                : undefined
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {eventsList.map((event) => (
              <EventCard
                key={event.code}
                event={event}
                onShare={handleShareEvent}
                onEdit={handleEditEvent}
                onDelete={handleDeleteEventClick}
              />
            ))}
          </div>
        )}
      </div>

      <EventModal
        show={showCreateModal}
        onClose={handleCloseCreateModal}
        onSubmit={handleSubmitEvent}
        isEditMode={false}
        isSubmitting={isSubmitting}
        onShowPaidTicketUpgrade={handleShowPaidTicketUpgrade}
      />

      <EventModal
        show={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleSubmitEvent}
        initialData={editEventData}
        isEditMode={true}
        isSubmitting={isSubmitting}
        onShowPaidTicketUpgrade={handleShowPaidTicketUpgrade}
      />

      <ShareEventModal
        show={showShareModal}
        onClose={handleCloseShareModal}
        eventName={selectedEventName}
        shareUrl={shareUrl}
      />

      <ConfirmModal
        show={
          showDeleteConfirmModal && permissions.can(Cap.EVENTS, Level.DELETE)
        }
        onClose={() => setShowDeleteConfirmModal(false)}
        title={t("components.eventPayments.deleteModal.title")}
        description={
          eventToDelete && eventsData[eventToDelete]
            ? t("components.eventPayments.deleteModal.description", {
                eventName:
                  eventsData[eventToDelete]?.event_data?.settings?.event_name,
              })
            : t("components.eventPayments.deleteModal.descriptionFallback")
        }
        cancelBtnName={t("components.eventPayments.actions.cancel")}
        cancelBtnFn={() => {
          setShowDeleteConfirmModal(false);
          setEventToDelete(null);
        }}
        confirmBtnName={t("components.eventPayments.deleteModal.title")}
        confirmBtnFn={handleDeleteEvent}
        ConfirmBtnColor="red"
        icon={<HiOutlineTrash size={24} className="text-red-500" />}
        iconBgColor="bg-red-100"
        iconColor="text-red-500"
      />

      <ModalShell
        show={showPaidTicketUpgradeModal}
        onClose={() => setShowPaidTicketUpgradeModal(false)}
        size="lg"
        title={t("components.eventPayments.paidTickets.upgradeTitle")}
      >
        <FeatureUpgradePrompt
          title={
            t("components.eventPayments.paidTickets.upgradeTitle") as string
          }
          description={
            t(
              "components.eventPayments.paidTickets.upgradeDescription"
            ) as string
          }
          benefitObjects={[
            {
              id: "benefit-1",
              text: t(
                "components.eventPayments.paidTickets.benefit1"
              ) as string,
            },
            {
              id: "benefit-2",
              text: t(
                "components.eventPayments.paidTickets.benefit2"
              ) as string,
            },
            {
              id: "benefit-3",
              text: t(
                "components.eventPayments.paidTickets.benefit3"
              ) as string,
            },
          ]}
          upgradeText={t("dashboard.features.upgrade.button") as string}
          onUpgrade={() => {
            setShowPaidTicketUpgradeModal(false);
            setTargetPlanTier(PlanTier.ENTERPRISE);
            setShowPlansModal(true);
          }}
          onCancel={() => setShowPaidTicketUpgradeModal(false)}
        />
      </ModalShell>

      <ModalShell
        show={showUnlockEventsModal}
        onClose={() => setShowUnlockEventsModal(false)}
        size="lg"
        title={t("components.eventPayments.freeUser.upgradeTitle")}
      >
        <FeatureUpgradePrompt
          title={t("components.eventPayments.freeUser.upgradeTitle") as string}
          description={
            t("components.eventPayments.freeUser.upgradeDescription") as string
          }
          benefitObjects={[
            {
              id: "benefit-1",
              text: t("components.eventPayments.freeUser.benefit1") as string,
            },
            {
              id: "benefit-2",
              text: t("components.eventPayments.freeUser.benefit2") as string,
            },
            {
              id: "benefit-3",
              text: t("components.eventPayments.freeUser.benefit3") as string,
            },
          ]}
          upgradeText={t("dashboard.features.upgrade.button") as string}
          onUpgrade={() => {
            setShowUnlockEventsModal(false);
            setTargetPlanTier(PlanTier.PREMIUM);
            setShowPlansModal(true);
          }}
          onCancel={() => setShowUnlockEventsModal(false)}
        />
      </ModalShell>

      <PlansModal
        showPlansModal={showPlansModal}
        setShowPlansModal={setShowPlansModal}
        title={t("components.eventPayments.plansModal.title") as string}
        targetPlan={targetPlanTier}
      />
    </div>
  );
};

export default EventPayments;
